import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Flex, Text, toast } from "@jsq/ds/core";
import { useFeatureFlag } from "@jsq/flags-react";
import { useGetFilterOptionsQuery } from "#graphql/supergraphHooks";
import { ControlTowerContext } from "#utils/context";
import { SPACING } from "#utils/spacing";
import { JIRA_PROJECT_KEY } from "./constants";
import { ControlTowerProvider, useControlTowerContext } from "./ControlTowerContext";
import {
  container,
  dashboardContainer,
  tableWrapper,
  tableWrapperWithTopPadding,
  workflowsHeader,
  workflowsHeaderTitle,
} from "./styles_vanilla.css";
import { TicketsTable } from "./TicketsTable";
import { ExtendedTicketFilters, isGridSetFilterTypeGuard } from "./TicketsTable/types";
import { WorkflowDashboard } from "./WorkflowDashboard/index";
import { useWorkflowFilterOptions } from "./WorkflowEngine/hooks/useWorkflowFilterOptions";

export interface ControlTowerProps {
  context: ControlTowerContext;
  /** Override the initially-active workflow tab. Defaults to "Assigned to me" when omitted. */
  defaultWorkflowTab?: string;
}

type ControlTowerContentProps = {
  defaultWorkflowTab?: string;
};

const ControlTowerContent: React.FC<ControlTowerContentProps> = ({ defaultWorkflowTab }) => {
  const context = useControlTowerContext();
  const [selectedWorkTypes, setSelectedWorkTypes] = useState<string[]>([]);
  const [selectedWorkTypeValues, setSelectedWorkTypeValues] = useState<string[]>([]);
  const [excludeAllCompleted, setExcludeAllCompleted] = useState(false);
  const [clearDashboardFilters, setClearDashboardFilters] = useState(false);
  const dashboardClickPendingRef = useRef(false);
  const pendingWorkTypeValuesRef = useRef<string[]>([]);
  const crltWorkflowEngine = useFeatureFlag("crltWorkflowEngine", false);

  const dashboardFilterModel = useMemo<Record<string, unknown> | null>(() => {
    if (!crltWorkflowEngine || selectedWorkTypeValues.length === 0) return null;
    return { workflowType: { filterType: "set", values: selectedWorkTypeValues } };
  }, [crltWorkflowEngine, selectedWorkTypeValues]);

  const { data: filterOptionsData } = useGetFilterOptionsQuery({
    variables: { projectKey: JIRA_PROJECT_KEY },
  });
  const availableWorkTypes = useMemo((): string[] => {
    if (!filterOptionsData?.filterOptions?.issuetype) {
      return [];
    }
    return filterOptionsData.filterOptions.issuetype.map((opt) => opt.value);
  }, [filterOptionsData]);

  const { filterOptions: workflowFilterOptionsData, loading: workflowFilterOptionsLoading } =
    useWorkflowFilterOptions();
  const availableWorkflowTypeValues = useMemo((): string[] => {
    return (workflowFilterOptionsData.workflowTypes ?? []).map((opt) => opt.value);
  }, [workflowFilterOptionsData]);

  const availableWorkTypesRef = useRef<string[]>(availableWorkTypes);
  useEffect(() => {
    availableWorkTypesRef.current = availableWorkTypes;
  }, [availableWorkTypes]);

  useEffect(() => {
    if (availableWorkTypes.length > 0 && pendingWorkTypeValuesRef.current.length > 0) {
      const validWorkTypes = pendingWorkTypeValuesRef.current.filter((wt) =>
        availableWorkTypes.includes(wt),
      );
      if (validWorkTypes.length > 0) {
        setSelectedWorkTypes(validWorkTypes);
      }
      pendingWorkTypeValuesRef.current = [];
    }
  }, [availableWorkTypes]);

  const isGPX = context === ControlTowerContext.GPX;
  const isCanopy = context === ControlTowerContext.CANOPY;

  const handleWorkTypeClick = useCallback(
    (workType: { name: string; value: string }, ticketCount: number) => {
      const isCurrentlySelected = selectedWorkTypes.includes(workType.name);

      if (isCurrentlySelected) {
        const newSelectedTypes = selectedWorkTypes.filter((wt) => wt !== workType.name);
        const newSelectedValues = selectedWorkTypeValues.filter((v) => v !== workType.value);
        setSelectedWorkTypes(newSelectedTypes);
        setSelectedWorkTypeValues(newSelectedValues);
        if (newSelectedTypes.length === 0) {
          setExcludeAllCompleted(false);
          setClearDashboardFilters(true);
        } else {
          dashboardClickPendingRef.current = true;
        }
        return;
      }

      const filterOptionsLoaded = crltWorkflowEngine
        ? !workflowFilterOptionsLoading
        : filterOptionsData?.filterOptions?.issuetype !== undefined;
      const workTypeAvailable = crltWorkflowEngine
        ? availableWorkflowTypeValues.includes(workType.value)
        : availableWorkTypes.includes(workType.name);

      if (!filterOptionsLoaded || !workTypeAvailable) {
        if (ticketCount === 0) {
          toast.error(`No tickets found for "${workType.name}".`);
        } else {
          toast.error(
            `Unable to filter by "${workType.name}". The filter options are being updated.`,
          );
        }
        return;
      }

      setClearDashboardFilters(false);
      setSelectedWorkTypes([...selectedWorkTypes, workType.name]);
      setSelectedWorkTypeValues([...selectedWorkTypeValues, workType.value]);
      setExcludeAllCompleted(true);
      dashboardClickPendingRef.current = true;
    },
    [
      filterOptionsData,
      availableWorkTypes,
      availableWorkflowTypeValues,
      workflowFilterOptionsLoading,
      selectedWorkTypes,
      selectedWorkTypeValues,
      crltWorkflowEngine,
    ],
  );

  const handleFilterChanged = useCallback(
    (event: { api: { getFilterModel: () => Record<string, unknown> } }) => {
      if (dashboardClickPendingRef.current) {
        dashboardClickPendingRef.current = false;
        return;
      }

      setExcludeAllCompleted(false);

      const filterModel = event.api.getFilterModel();
      const workTypeFilter = filterModel?.workType;
      const workTypeValues = isGridSetFilterTypeGuard(workTypeFilter)
        ? workTypeFilter.values
        : [];

      pendingWorkTypeValuesRef.current = workTypeValues;

      const currentAvailableWorkTypes = availableWorkTypesRef.current;
      const validWorkTypes = workTypeValues.filter((wt) =>
        currentAvailableWorkTypes.includes(wt),
      );

      setSelectedWorkTypes(validWorkTypes);
    },
    [],
  );

  const handleClearFilters = useCallback(() => {
    setSelectedWorkTypes([]);
    setSelectedWorkTypeValues([]);
  }, []);

  const handleSavedViewFilterApplied = useCallback((filterModel: Record<string, unknown>) => {
    const workTypeFilter = filterModel?.workType;
    const workTypeValues = isGridSetFilterTypeGuard(workTypeFilter)
      ? workTypeFilter.values
      : [];

    pendingWorkTypeValuesRef.current = workTypeValues;

    const currentAvailableWorkTypes = availableWorkTypesRef.current;
    const validWorkTypes = workTypeValues.filter((wt) =>
      currentAvailableWorkTypes.includes(wt),
    );

    setSelectedWorkTypes(validWorkTypes);
  }, []);

  const initialFilters: ExtendedTicketFilters | undefined = useMemo(() => {
    if (selectedWorkTypes.length === 0) {
      return undefined;
    }
    return {
      workTypes: selectedWorkTypes,
    };
  }, [selectedWorkTypes]);

  return (
    <div data-testid="control-tower-root" className={container}>
      {isGPX && !crltWorkflowEngine && (
        <Flex
          className={workflowsHeader}
          justifyContent="space-between"
          alignItems="center"
          gap={SPACING.SMALL}
        >
          <Text className={workflowsHeaderTitle} variant={isGPX ? "h1" : "regular-xl"}>
            Workflows
          </Text>
          <Button
            variant="primary"
            icon="plus"
            onClick={() => {
              window.location.href = "/request-a-service";
            }}
          >
            New request
          </Button>
        </Flex>
      )}
      {isCanopy && (
        <div className={dashboardContainer}>
          <WorkflowDashboard
            onWorkTypeClick={handleWorkTypeClick}
            selectedWorkTypes={selectedWorkTypes}
          />
        </div>
      )}
      <div
        className={`${tableWrapper} ${isGPX && crltWorkflowEngine ? tableWrapperWithTopPadding : ""}`}
      >
        <TicketsTable
          initialFilters={initialFilters}
          onFilterChanged={handleFilterChanged}
          onSavedViewFilterApplied={handleSavedViewFilterApplied}
          onClearFilters={handleClearFilters}
          excludeAllCompleted={excludeAllCompleted}
          clearDashboardFilters={clearDashboardFilters}
          defaultActiveTabKey={defaultWorkflowTab}
          dashboardFilterModel={dashboardFilterModel}
        />
      </div>
    </div>
  );
};

/**
 * Main Control Tower component with context provider.
 * This component provides a unified view of workflows from Jira, with context-aware
 * features for GPX (client-facing) and Canopy (internal FA/IS) environments.
 */
export const ControlTower: React.FC<ControlTowerProps> = ({ context, defaultWorkflowTab }) => {
  return (
    <ControlTowerProvider context={context}>
      <ControlTowerContent defaultWorkflowTab={defaultWorkflowTab} />
    </ControlTowerProvider>
  );
};

export default ControlTower;
